import { InactivityModal } from "@/components/inactivityModal";
import LoadingScreen from "@/components/loading-screen";
import { useGetAppConfig } from "@/queries/config/config.api";
import { removeCookie, setCookie } from "@/utils/cookie";
import { getEnv } from "@/utils/runtime-config";
import Keycloak from "keycloak-js";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import * as Sentry from "@sentry/react";

const keycloak = new Keycloak({
  url: getEnv("VITE_KEYCLOAK_URL") || "",
  realm: getEnv("VITE_KEYCLOAK_REALM") || "",
  clientId: getEnv("VITE_KEYCLOAK_CLIENT_ID") || "",
});

type AuthContextType = {
  keycloak: Keycloak;
  authenticated: boolean;
  initialized: boolean;
  authUser: AuthUser | null;
  permissions: string[];
  hasPermission?: (permission: string) => boolean;
  appConfig?: {
    logo?: string;
    institution_name?: string;
  } | null;
  configLoaded?: boolean;
  getUserRoleType: () => "admin" | "editor" | "reader" | "empty";
};

type AuthUser = {
  id: string;
  name: string;
  family_name: string;
  given_name: string;
  email: string;
};

const AuthContext = createContext<AuthContextType>({
  keycloak,
  authenticated: false,
  initialized: false,
  authUser: null,
  permissions: [],
  hasPermission: () => {
    console.warn("hasPermission not implemented");
    return false;
  },
  appConfig: null,
  configLoaded: false,
  getUserRoleType: () => "empty",
});

const INACTIVITY_MINUTES = Number(getEnv("VITE_INACTIVITY_MINUTES") || 14);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const initialized = useRef<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);

  // Hook para obtener configuración de la app
  const {
    data: appConfig,
    isLoading: configLoading,
    isError: configError,
  } = useGetAppConfig();

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const updateFromToken = () => {
    if (keycloak?.token && keycloak?.tokenParsed) {
      setAuthUser({
        id: keycloak.tokenParsed.sub || "",
        name: keycloak.tokenParsed.name || "",
        family_name: keycloak.tokenParsed.family_name || "",
        given_name: keycloak.tokenParsed.given_name || "",
        email: keycloak.tokenParsed.email || "",
      });
      Sentry.setUser({
        email: keycloak.tokenParsed.email || "",
        id: keycloak.tokenParsed.sub || "",
        username: `${keycloak.tokenParsed.given_name || ""} ${keycloak.tokenParsed.family_name || ""}`,
      });
      const resourceAccess =
        keycloak?.tokenParsed?.resource_access?.[
          getEnv("VITE_BACKEND_APP_NAME")
        ];
      if (resourceAccess) {
        setPermissions(resourceAccess?.roles || []);
      }
      setCookie("keycloakToken", keycloak?.token);
    }
  };

  const [showModal, setshowModal] = useState(false);

  useEffect(() => {
    let temporizador: ReturnType<typeof setTimeout>;
    let contadorIntervalo: ReturnType<typeof setInterval> | null = null;
    let ultimaActualizacionLigera = 0;

    const INACTIVITY_SECONDS = 60 * INACTIVITY_MINUTES;
    const INACTIVITY_MS = INACTIVITY_SECONDS * 1000;
    const THROTTLE_INTERVAL = 500;

    const reiniciarTemporizador = (evento: Event) => {
      const ahora = Date.now();
      const tipoEvento = evento.type;

      const esEventoLigero = ["mousemove", "scroll", "wheel"].includes(
        tipoEvento,
      );
      if (
        esEventoLigero &&
        ahora - ultimaActualizacionLigera < THROTTLE_INTERVAL
      ) {
        return;
      }
      if (esEventoLigero) {
        ultimaActualizacionLigera = ahora;
      }

      localStorage.setItem("activity", ahora.toString());

      clearTimeout(temporizador);
      if (contadorIntervalo) {
        clearInterval(contadorIntervalo);
        contadorIntervalo = null;
      }

      let segundosRestantes = INACTIVITY_SECONDS;
      // console.log(`Modal aparecerá en ${segundosRestantes} s`);

      contadorIntervalo = setInterval(() => {
        segundosRestantes -= 1;
        // console.log(`Modal aparecerá en ${segundosRestantes} s`);
        if (segundosRestantes <= 0 && contadorIntervalo) {
          clearInterval(contadorIntervalo);
          contadorIntervalo = null;
        }
      }, 1000);

      temporizador = setTimeout(() => {
        localStorage.setItem("isOpen", ahora.toString());
        window.dispatchEvent(
          new CustomEvent("isOpen-local-change", { detail: ahora.toString() }),
        );
        setshowModal(true);
      }, INACTIVITY_MS);
    };

    const eventos: (keyof DocumentEventMap)[] = [
      "mousemove",
      "keydown",
      "mousedown",
      "touchstart",
      "scroll",
      "wheel",
    ];
    eventos.forEach((ev) => window.addEventListener(ev, reiniciarTemporizador));

    reiniciarTemporizador(new Event("init"));

    return () => {
      clearTimeout(temporizador);
      if (contadorIntervalo) clearInterval(contadorIntervalo);
      eventos.forEach((ev) =>
        window.removeEventListener(ev, reiniciarTemporizador),
      );
    };
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    // 1 second delay to ensure Keycloak is initialized before checking auth
    const timer = setTimeout(async () => {
      try {
        const authenticated = await keycloak.init({
          onLoad: "check-sso",
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          checkLoginIframe: false, // Disable iframe checks for simplicity
          pkceMethod: "S256", // Use PKCE for security
        });
        initialized.current = true;
        setIsAuthenticated(authenticated);

        if (authenticated && keycloak.tokenParsed) {
          updateFromToken();
          keycloak.onAuthRefreshSuccess = () => {
            updateFromToken();
          };
          keycloak.onAuthRefreshError = () => {
            console.error("Keycloak token refresh failed");
            keycloak.login(); // Redirect to login if refresh fails
          };
          keycloak.onAuthLogout = () => {
            setAuthUser(null);
            setPermissions([]);
            setIsAuthenticated(false);
            removeCookie("keycloakToken");
          };
        } else {
          setAuthUser(null);
          removeCookie("keycloakToken");
          keycloak.login();
        }
      } catch (e) {
        console.error("Error initializing Keycloak:", e);
        initialized.current = true;
      } finally {
        setIsLoading(false);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (initialized.current && keycloak.didInitialize) {
        try {
          await keycloak.updateToken(-1);
          updateFromToken();
        } catch (error) {
          console.error("Error updating Keycloak token:", error);
        }
      }
    }, 1000 * 30); // 1 minute delay to ensure Keycloak is initialized before checking auth

    return () => clearInterval(timer);
  }, [initialized]);

  // Mostrar loading hasta que tanto la autenticación como la configuración estén listas
  const configLoaded = !configLoading && !configError;
  const shouldShowLoading =
    isLoading || authUser === null || (isAuthenticated && !configLoaded);

  const getUserRoleType = useCallback(() => {
    if (!authUser || !permissions) return "empty";
    let type: "admin" | "editor" | "reader" | "empty" = "empty";
    if (permissions.length === 0) {
      return type;
    }
    if (permissions.some((permission) => permission.includes("read"))) {
      type = "reader";
    }
    if (
      permissions.some((permission) => {
        return (
          permission.includes("update") ||
          permission.includes("edit") ||
          permission.includes("create")
        );
      })
    ) {
      type = "editor";
    }
    if (
      permissions.some((permission) => {
        return (
          permission.includes("roles:create") ||
          permission.includes("roles:update") ||
          permission.includes("roles:delete") ||
          permission.includes("roles:assign-permissions") ||
          permission.includes("users:delete") ||
          permission.includes("users:assign-role") ||
          permission.includes("users:activate") ||
          permission.includes("users:create") ||
          permission.includes("users:update") ||
          permission.includes("users:reset-password")
        );
      })
    ) {
      type = "admin";
    }
    if (type === "empty") {
      type = "editor"; // Default to editor if no permissions match
    }
    return type;
  }, [authUser, permissions]);

  if (shouldShowLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <InactivityModal
        onClose={() => {
          setshowModal(false);
        }}
        isOpen={showModal}
      />
      <AuthContext.Provider
        value={{
          keycloak: keycloak,
          authenticated: isAuthenticated,
          initialized: initialized.current,
          authUser,
          permissions,
          hasPermission,
          appConfig: appConfig || null,
          configLoaded,
          getUserRoleType,
        }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

// eslint-disable-next-line react-refresh/only-export-components
export const getKeycloakInstance = () => keycloak;
